import { useEffect, useState } from 'preact/hooks';
import { RESOURCES, bankRate, hasResources, res, totalResources, type Action, type GameView, type Resource, type Resources } from '../engine';
import type { GameClient } from './game-client';
import { RESOURCE_COLORS, RESOURCE_LABELS, RESOURCE_TEXT } from './theme';
import { PlayerName, ResourceChip, ResourceList, ResourcePicker } from './widgets';

/**
 * Only what you can actually trade: a row per resource you hold enough of at
 * your best rate (4:1, or 3:1 / 2:1 with a harbor), with one button per thing
 * the bank can give back.
 */
function BankTrades({ legal, view, seat, run }: { legal: Action[]; view: GameView; seat: number; run: (a: Action) => void }) {
  const options = new Map<Resource, Resource[]>();
  for (const a of legal) {
    if (a.type !== 'bankTrade') continue;
    options.set(a.give, [...(options.get(a.give) ?? []), a.receive]);
  }
  if (options.size === 0) {
    return (
      <p class="muted small" data-testid="bank-none">
        Bank trades need 4 of one resource (3 with a 3:1 harbor, 2 with a matching 2:1 harbor).
      </p>
    );
  }
  return (
    <div class="bank-trades" data-testid="bank-trades">
      <h3>Trade with the bank</h3>
      {[...options.entries()].map(([give, receives]) => (
        <div class="bank-row" key={give}>
          <span class="bank-give">
            Give {bankRate(view, seat, give)} <ResourceChip resource={give} /> for
          </span>
          <span class="row wrap">
            {receives.map((r) => (
              <button
                key={r}
                type="button"
                class="chip-button"
                data-testid={`bank-${give}-${r}`}
                style={{ background: RESOURCE_COLORS[r], color: RESOURCE_TEXT[r] }}
                onClick={() => run({ type: 'bankTrade', give, receive: r })}
              >
                1 {RESOURCE_LABELS[r]}
              </button>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TradePanel({ client, seat, run }: { client: GameClient; seat: number; run: (a: Action, as?: number) => void }) {
  const { view, legal, names } = client;
  const [offerGive, setOfferGive] = useState(res());
  const [offerWant, setOfferWant] = useState(res());
  useEffect(() => {
    setOfferGive(res());
    setOfferWant(res());
  }, [view.turn]);
  if (view.phase.kind !== 'main') return null;
  const trade = view.trade;
  const myTurn = seat === view.currentPlayer;
  const hand = view.players[seat]!.resources ?? res();

  return (
    <div class="panel trade">
      <h2>Trade</h2>
      {trade && (
        <div class="offer" data-testid="open-trade">
          <p>
            <PlayerName seat={trade.from} names={names} /> gives <ResourceList resources={trade.give} /> for{' '}
            <ResourceList resources={trade.want} />
          </p>
          <TradeSummary client={client} />
          {(() => {
            const row = (i: number) => (
              <TradeResponseRow
                key={i}
                seat={i}
                client={client}
                canAct={(client.hotSeat || i === seat) && !client.cpu?.[i]}
                proposerIsMe={myTurn}
                run={run}
              />
            );
            const others = view.players.map((_, i) => i).filter((i) => i !== trade.from);
            // Up front: answers the proposer can act on, and your own answer. Everyone else is one tap away.
            const upFront = (i: number) => {
              const r = trade.responses[i] ?? null;
              return (myTurn && r !== null && r.kind !== 'decline') || (i === seat && !client.cpu?.[i]);
            };
            const rest = others.filter((i) => !upFront(i));
            const answerable = rest.some((i) => client.hotSeat && !client.cpu?.[i] && trade.responses[i] === null);
            return (
              <>
                {others.filter(upFront).map(row)}
                {rest.length > 0 && (
                  <details class="trade-responses" data-testid="trade-responses">
                    <summary>{answerable ? 'Answer for other players' : `All responses (${rest.length})`}</summary>
                    {rest.map(row)}
                  </details>
                )}
              </>
            );
          })()}
          {myTurn && (
            <button class="link" data-testid="cancel-trade" onClick={() => run({ type: 'cancelTrade' })}>
              Cancel offer
            </button>
          )}
        </div>
      )}

      {myTurn && !trade && (
        <>
          <BankTrades legal={legal} view={view} seat={seat} run={run} />
          <details class="offer-composer">
            <summary>Offer a trade to players</summary>
            <div class="composer">
              <div>
                <h3>You give</h3>
                <ResourcePicker value={offerGive} onChange={setOfferGive} max={hand} testid="offer-give" />
              </div>
              <div>
                <h3>You want</h3>
                <ResourcePicker value={offerWant} onChange={setOfferWant} testid="offer-want" />
              </div>
            </div>
            <button
              data-testid="offer-trade"
              disabled={!hasResources(hand, offerGive) || totalResources(offerGive) === 0 || totalResources(offerWant) === 0}
              onClick={() => {
                run({ type: 'offerTrade', give: offerGive, want: offerWant });
                setOfferGive(res());
                setOfferWant(res());
              }}
            >
              Offer
            </button>
          </details>
        </>
      )}
    </div>
  );
}

function capTo(r: Resources, max: Resources | undefined): Resources {
  if (!max) return r;
  const out = { ...r };
  for (const k of RESOURCES) out[k] = Math.min(out[k], max[k]);
  return out;
}

function TradeResponseRow({
  seat,
  client,
  canAct,
  proposerIsMe,
  run,
}: {
  seat: number;
  client: GameClient;
  canAct: boolean;
  proposerIsMe: boolean;
  run: (a: Action, as?: number) => void;
}) {
  const { view, names } = client;
  // Who's composing on this row: the responder countering the offer, or the proposer countering their counter.
  const [composing, setComposing] = useState<'responder' | 'proposer' | null>(null);
  const [give, setGive] = useState(res());
  const [want, setWant] = useState(res());
  const trade = view.trade!;
  const r = trade.responses[seat] ?? null;
  const ownHand = view.players[seat]!.resources ?? client.handOf?.(seat);
  const proposerHand = view.players[trade.from]!.resources ?? client.handOf?.(trade.from);
  // A new offer clears every response, so close any composer left open from the last round.
  useEffect(() => setComposing(null), [trade.id]);

  const status =
    r === null ? (
      canAct ? null : <span class="muted">thinking…</span>
    ) : r.kind === 'accept' ? (
      <span class="muted">accepted</span>
    ) : r.kind === 'decline' ? (
      <span class="muted">declined</span>
    ) : (
      <span class="small" data-testid={`counter-terms-${seat}`}>
        counters: gives <ResourceList resources={r.give} /> for <ResourceList resources={r.want} />
      </span>
    );

  // The responder can answer, or change their answer, until the proposer picks someone.
  const responderButtons = canAct && (
    <>
      {r?.kind !== 'accept' && (
        <button class="small" data-testid={`accept-${seat}`} onClick={() => run({ type: 'respondTrade', accept: true }, seat)}>
          Accept
        </button>
      )}
      {r?.kind !== 'decline' && (
        <button class="small secondary" data-testid={`decline-${seat}`} onClick={() => run({ type: 'respondTrade', accept: false }, seat)}>
          Decline
        </button>
      )}
      <button
        class="small secondary"
        data-testid={`counter-${seat}`}
        onClick={() => {
          // Start from their current counter, or the mirror of the offer (capped by what they hold).
          setGive(capTo({ ...(r?.kind === 'counter' ? r.give : trade.want) }, ownHand));
          setWant({ ...(r?.kind === 'counter' ? r.want : trade.give) });
          setComposing(composing === 'responder' ? null : 'responder');
        }}
      >
        {r?.kind === 'counter' ? 'Change counter' : 'Counter'}
      </button>
    </>
  );

  const proposerButtons = proposerIsMe && r && r.kind !== 'decline' && (
    <>
      <button class="small primary" data-testid={`confirm-${seat}`} onClick={() => run({ type: 'confirmTrade', with: seat })}>
        {r.kind === 'accept' ? `Trade with ${names[seat]}` : 'Accept counter'}
      </button>
      {r.kind === 'counter' && (
        <button
          class="small secondary"
          data-testid={`recounter-${seat}`}
          onClick={() => {
            // Seen from the proposer's side: they give what the counter wants, and want what it gives.
            setGive(capTo({ ...r.want }, proposerHand));
            setWant({ ...r.give });
            setComposing(composing === 'proposer' ? null : 'proposer');
          }}
        >
          Counter back
        </button>
      )}
    </>
  );

  const asProposer = composing === 'proposer';
  const composerHand = asProposer ? proposerHand : ownHand;
  const composerName = asProposer ? 'You' : names[seat];
  return (
    <div class="response">
      <div class="row between wrap">
        <PlayerName seat={seat} names={names} />
        <span class="row wrap">
          {status}
          {responderButtons}
          {proposerButtons}
        </span>
      </div>
      {composing && (
        <div class="composer" data-testid={asProposer ? `recounter-form-${seat}` : `counter-form-${seat}`}>
          {composerHand && (
            <p class="small muted">
              {asProposer ? 'You have' : `${names[seat]} has`} <ResourceList resources={composerHand} />
            </p>
          )}
          <div>
            <h3>{composerName} give{asProposer ? '' : 's'}</h3>
            <ResourcePicker value={give} onChange={setGive} max={composerHand} testid={`${asProposer ? 'recounter' : 'counter'}-give-${seat}`} />
          </div>
          <div>
            <h3>{composerName} want{asProposer ? '' : 's'}</h3>
            <ResourcePicker value={want} onChange={setWant} testid={`${asProposer ? 'recounter' : 'counter'}-want-${seat}`} />
          </div>
          {asProposer && <p class="small muted">This replaces your offer for everyone.</p>}
          <button
            data-testid={asProposer ? `recounter-submit-${seat}` : `counter-submit-${seat}`}
            disabled={totalResources(give) === 0 || totalResources(want) === 0}
            onClick={() => {
              setComposing(null);
              if (asProposer) run({ type: 'offerTrade', give, want });
              else run({ type: 'counterTrade', give, want }, seat);
            }}
          >
            {asProposer ? 'Send new offer' : 'Send counter-offer'}
          </button>
        </div>
      )}
    </div>
  );
}
/** One line on where the offer stands: who's in, who countered, who's still deciding. */
function TradeSummary({ client }: { client: GameClient }) {
  const { view, names } = client;
  const trade = view.trade!;
  const who = (kind: 'accept' | 'counter' | 'decline' | null) =>
    trade.responses.flatMap((r, i) => (i !== trade.from && (r?.kind ?? null) === kind ? [names[i] ?? `Player ${i + 1}`] : []));
  const accepted = who('accept');
  const countered = who('counter');
  const declined = who('decline');
  const waiting = who(null);
  const parts: string[] = [];
  if (accepted.length) parts.push(`Accepted: ${accepted.join(', ')}`);
  if (countered.length) parts.push(`Countered: ${countered.join(', ')}`);
  if (waiting.length) parts.push(`Thinking: ${waiting.length === 1 ? waiting[0] : `${waiting.length} players`}`);
  if (declined.length && (accepted.length || countered.length || waiting.length)) parts.push(`${declined.length} declined`);
  const text = parts.length ? parts.join(' · ') : 'Everyone declined.';
  return (
    <p class="trade-summary" data-testid="trade-summary" aria-live="polite">
      {text}
    </p>
  );
}
